import { AfterViewInit, Component, ErrorHandler, Injectable, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, Injector, inject, signal } from '@angular/core'
import { FieldTree, form } from '@angular/forms/signals'
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog'
import { MatPaginator } from '@angular/material/paginator'
import { ActivatedRoute, Router } from '@angular/router'
import { KirjanpitajaService } from 'app/_angular/service/kirjanpitaja/kirjanpitaja.service'
import { LaskutusHakuStateService } from 'app/_jaettu-angular/laskut/haku/laskutus-haku-state.service'

import { LoadingConfig } from 'app/_jaettu-angular/_components/loading.component'
import { LaskuKatseleComponentDataResolve, LaskuKatseleComponentExistingData } from '../../_angular/_resolvers/lasku.resolve'
import { LaskuService } from '../../_angular/service/lasku/lasku.service'

import { WindowSizeService } from '../../_jaettu-angular/service/window.service'

import { Lasku, LaskuBase, LaskunAsiakas, LaskunListaustietorivi, LaskunTila, LaskuTotalsPerCurrency } from '@lemon/core-firebase/model/lasku'

import { LaskunLokalisoituTila } from '../laskut.firestore.datasource'
import { LaskuSharedService } from '@lemon/core-firebase/service/lasku/lasku-shared.service'
import { NumberMonth } from '@lemon/core/model/common'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { DateService } from '@lemon/core/service/date.service'

import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonator.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { lemonShare } from 'app/_jaettu-angular/_rxjs/lemon-share.operator'
import { Asiakas } from 'app/_jaettu-lemonator/model/asiakas'
import { BehaviorSubject, combineLatest, EMPTY, firstValueFrom, iif, Observable, of, Subject } from 'rxjs'
import { map, startWith, switchMap, takeUntil, throttleTime } from 'rxjs/operators'
import { Kirjanpitaja } from '../../_jaettu-lemonator/model/kirjanpitaja'
import { LaskuKommenttiDialog } from '../dialogit/lasku-kommentti-lemonator.dialog'
import {
  LaskuHaluatkoVarmastiPeruaMaksetuksiMerkitsemisenLemonatorDialog,
  LaskuHaluatkoVarmastiPeruaMaksetuksiMerkitsemisenLemonatorDialogData,
} from '../dialogit/lasku.haluatko-varmasti-perua-maksetuksi-merkitsemisen-lemonator.dialog'
import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { LaskunLokalisoituKuukausi, LaskutSelaaDataSourceService } from './laskut-selaa.datasource.service'
import { annaPaiviaMyohassa, laskeLaskulistanYhteensaRivit, LaskulistanYhteensaRivi } from './laskut-selaa.logiikka'

export type LaskuProperties = 'nro' | 'pvm' | 'erapvm' | 'summa' | 'avoinna' | 'tila' | 'lataa' | undefined

export interface SummienRullausData {
  juurilasku: Lasku
  kasiteltava: LaskuBase
  edellinenLaskunTila: LaskunTila
}

interface LaskuhakuLomake {
  tila: LaskunTila | null
  kuukausi: NumberMonth | null
}

interface NaytettavaLaskurivi extends LaskunListaustietorivi {
  paiviaMyohassa: number | null
}

export interface LaskuKommenttiDialogData {
  lasku: Lasku
  kirjanpitaja: Kirjanpitaja
}

@Component({
  templateUrl: './laskut-selaa.component.html',
  styleUrls: ['./laskut-selaa.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LaskutSelaaComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly asiakas = signal<Asiakas>(null)

  // @ViewChild('laskutaulu', { static: true }) laskutaulu: ElementRef
  @ViewChild(MatPaginator, { static: true }) _paginator: MatPaginator

  private _ngUnsubscribe = new Subject<void>()
  private readonly _injector = inject(Injector)
  // private _taydennaAsetuksetDialogi: MatDialogRef<LaskuTaydennaAsetuksetDialog> = null
  private _kommenttiDialogi: MatDialogRef<LaskuKommenttiDialog> = null

  lomake: FieldTree<LaskuhakuLomake>
  readonly hakuAvoinna = signal(false)

  // propertiesToDisplay: TuoteProperties[] = []
  laskunTilatObservable: Observable<LaskunLokalisoituTila[]>
  kuukausivaihtoehdotObservable: Observable<LaskunLokalisoituKuukausi[]>
  tableViewOptionsObservable: Observable<{ showAddButton: boolean; showCommentsColumn: boolean }>

  nakyvatRivitObservable: Observable<NaytettavaLaskurivi[]>
  yhteensaRivitObservable: Observable<LaskulistanYhteensaRivi[]>

  naytaEiYhtaanObservable: Observable<boolean>
  lataaObservable: Observable<boolean>

  countToAvoinna: number = 0
  countToEraantynyt: number = 0
  readonly avoinnaTo = signal(0)
  readonly avoinnaFrom = signal(0)
  readonly eraantynytTo = signal(0)
  readonly eraantynytFrom = signal(0)
  readonly valuutta = signal('')

  private _alustaSummat = true
  private _pauser = new Subject<boolean>()

  loadingSettings: LoadingConfig = {
    fullScreenBackdrop: false,
    backdropBackgroundColour: 'rgba(0,0,0,0.15)',
    backdropBorderRadius: '4px',
    primaryColour: 'rgba(255, 255, 255, 1)',
    secondaryColour: 'rgba(255, 255, 255, 1)', // rgba(252, 238, 32, .15)
    tertiaryColour: 'rgba(255, 255, 255, 1)',
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private errorHandler: ErrorHandler,
    private dialog: MatDialog,
    private laskuService: LaskuService,
    private asiakasService: AsiakasService,
    private laskuSummatService: LaskuSummatService,
    private laskuKatseleComponentDataResolve: LaskuKatseleComponentDataResolve,
    private windowSizeService: WindowSizeService,
    private _dataSource: LaskutSelaaDataSourceService,
    // private versionTarkistaja: VersionTarkistusPalvelu,
    // private asetustenOletuksetService: AsetustenOletuksetService
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _kirjanpitajaService: KirjanpitajaService,
    private _hakuStateService: LaskutusHakuStateService,
    private _dateService: DateService,
    private _laskuSharedService: LaskuSharedService,
    private _currencyService: CurrencyService,
  ) {
    this.lataaObservable = _dataSource.lataaObservable
    this.naytaEiYhtaanObservable = combineLatest([this._dataSource.rivienMaaraObservable, this._dataSource.lataaObservable]).pipe(
      map(([rivienMaara, lataa]) => {
        if (lataa) {
          return false
        }
        return rivienMaara < 1
      }),
    )
  }

  ngOnInit() {
    if (this._hakuStateService.lasku.onkoHakutilaTallennettu(this.router.url)) {
      this.hakuAvoinna.set(true)
    }

    this.asiakasService.nykyinenAsiakasObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(asiakas => this.asiakas.set(asiakas))

    // this.versionTarkistaja.sovelluksenVersioObservable.pipe(
    //   takeUntil(this._ngUnsubscribe)
    // ).subscribe(versiotiedot => {
    //   this.versionTarkistaja.tarkistaVersio(versiotiedot)
    // })

    this.asetaListauksenKolumnit()

    // this.laskuService.asetuksetObservable.pipe(
    //   takeUntil(this.ngUnsubscribe)
    // ).subscribe(asetukset => {
    //   if (asetukset && !asetukset.summahuomautusPiilotettu) {
    //     this.naytaHuomautus = true
    //   } else {
    //     this.naytaHuomautus = false
    //   }
    // }, error => {
    //   this.errorHandler.handleError(error)
    // })

    this._pauser
      .pipe(
        switchMap(aktiivinen => {
          return iif(() => aktiivinen, this.laskuSummatService.summatObservable, EMPTY)
        }),
        throttleTime(1100, undefined, { leading: true, trailing: true }),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(
        summat => {
          // console.log('summat', JSON.stringify(summat))
          if (summat) {
            // Mikä valuutta
            this.valuutta.set(summat.valuutta)

            // Mistä rullataan
            this.avoinnaFrom.set(summat.avoinnaAlkuperainen)
            this.eraantynytFrom.set(summat.eraantynytAlkuperainen)

            // Mihin rullataan
            if (this._alustaSummat) {
              this._alustaSummat = false
              setTimeout(() => {
                this.avoinnaTo.set(summat.avoinna)
                this.eraantynytTo.set(summat.eraantynyt)
              }, 150)
            } else {
              this.avoinnaTo.set(summat.avoinna)
              this.eraantynytTo.set(summat.eraantynyt)
            }
          }
        },
        error => {
          this.errorHandler.handleError(error)
        },
      )
    this._pauser.next(true)

    this.laskunTilatObservable = this.dataSource.laskunTilatObservable
    this.kuukausivaihtoehdotObservable = this.dataSource.kuukausivaihtoehdotObservable
    this.tableViewOptionsObservable = this.asiakasService.nykyinenAsiakasAvainObservable.pipe(
      map(asiakas => {
        return { showAddButton: false, showCommentsColumn: asiakas?.asiakasId === 9 }
      }),
      startWith({ showAddButton: true, showCommentsColumn: false }),
    )

    // Luo form, HUOM! Default arvot laitetaan hakutiedotObservable subscribessa
    this.lomake = form(
      signal<LaskuhakuLomake>({
        tila: null,
        kuukausi: null,
      }),
      { injector: this._injector },
    )

    // Jos hakutiedot muuttuvat, muuta käyttöliittymää sen mukaan
    this.dataSource.hakutiedotObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(hakutiedot => {
      if (this.lomake.tila().value() !== hakutiedot.hakukriteerit.tila) {
        asetaArvo(this.lomake.tila, hakutiedot.hakukriteerit.tila)
      }
      if (this.lomake.kuukausi().value() !== hakutiedot.hakukriteerit.kuukausi) {
        asetaArvo(this.lomake.kuukausi, hakutiedot.hakukriteerit.kuukausi)
      }
    })

    arvonMuutokset(this.lomake.tila, { injector: this._injector }).subscribe((value: LaskunTila) => {
      this.dataSource.tila = value
    })

    arvonMuutokset(this.lomake.kuukausi, { injector: this._injector }).subscribe((value: NumberMonth | null) => {
      this.dataSource.kuukausi = value
    })

    this._paginator.page.subscribe(
      page => {
        this.dataSource.page = page
      },
      error => {
        this.errorHandler.handleError(error)
      },
    )

    // this.route.data.pipe(
    //   takeUntil(this._ngUnsubscribe)
    // ).subscribe((data: { data: LaskutComponentData }) => {
    //   if (data && data.data && data.data.nollaaHakuasetukset) {
    //     this.dataSource.resetSearchToDefaults()
    //   }
    // }, error => {
    //   this.errorHandler.handleError(error)
    // })

    this.nakyvatRivitObservable = this.dataSource.dataSourceTransformed.connect(null).pipe(
      map(laskurivit => laskurivit.map(laskurivi => this._annaNaytettavaLaskurivi(laskurivi))),
      lemonShare(),
    )
    this.yhteensaRivitObservable = this.nakyvatRivitObservable.pipe(
      map(laskut => laskeLaskulistanYhteensaRivit(laskut, lasku => this._annaRivinEurokurssi(lasku), this._currencyService)),
      lemonShare(),
    )
  }

  private _annaNaytettavaLaskurivi(laskurivi: LaskunListaustietorivi): NaytettavaLaskurivi {
    return { ...laskurivi, paiviaMyohassa: annaPaiviaMyohassa(laskurivi, this._dateService.currentLocalDate(), this._dateService) }
  }

  private _annaRivinEurokurssi(lasku: LaskunListaustietorivi): number | undefined {
    if (lasku.valuutta === 'EUR' || !lasku.juurilasku) {
      return undefined
    }
    return this._laskuSharedService.annaViimeisinTavallinenLasku(lasku.juurilasku)?.valuuttakonversio?.kurssi?.rate
  }

  get dataSource(): LaskutSelaaDataSourceService {
    return this._dataSource
  }

  ngAfterViewInit() {
    this.windowSizeService.sizeObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(koko => {
      setTimeout(() => {
        this.asetaListauksenKolumnit()
      }, 250)
    })
  }

  private asetaListauksenKolumnit() {
    // this.naytaNro = true
    // this.naytaPvm = true
    // this.naytaSumma = true
    // const leveys = this.laskutaulu.nativeElement.offsetWidth
    // // if (tablenKoko < 500) {
    // //   this.propertiesToDisplay = [               'asiakas',         'erapvm',         'avoinna', 'merkitse']
    // // } else
    // if (leveys < 600) {
    //   this.propertiesToDisplay = ['tila', 'nro', 'asiakas', 'erapvm', 'avoinna', 'merkitse']
    //   // this.naytaNro = false
    //   this.naytaPvm = false
    //   this.naytaSumma = false
    // } else if (leveys < 700) {
    //   this.propertiesToDisplay = ['tila', 'nro', 'asiakas', 'erapvm', 'summa', 'avoinna', 'merkitse']
    //   // this.naytaNro = false
    //   this.naytaPvm = false
    // } else if (leveys < 800) {
    //   this.propertiesToDisplay = ['tila', 'nro', 'asiakas', 'pvm', 'erapvm', 'summa', 'avoinna', 'merkitse']
    //   // this.naytaNro = false
    // } else {
    //   this.propertiesToDisplay = ['tila', 'nro', 'asiakas', 'pvm', 'erapvm', 'summa', 'avoinna', 'merkitse']
    // }
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  getDisplayFn() {
    return val => this.display(val)
  }

  private display(laskunAsiakas: LaskunAsiakas): string {
    if (laskunAsiakas && laskunAsiakas.nimi) {
      return laskunAsiakas.nimi
    }
    return ''
  }

  onCountToEnd() {
    // this.laskuSummatService.numerotRullattiin()
  }

  avaaHaku() {
    this.hakuAvoinna.set(true)
  }

  suljeHaku() {
    this.hakuAvoinna.set(false)
  }

  async muokkaa(rivi: LaskunListaustietorivi) {
    const existing: LaskuKatseleComponentExistingData = {
      juurilasku: rivi.juurilasku,
      kasiteltava: rivi.kasiteltava,
    }
    this.laskuKatseleComponentDataResolve.asetaOlemassaolevaData(existing)
    const nykyinenAsiakas = await firstValueFrom(this.asiakasService.nykyinenAsiakasAvainObservable)
    this.router.navigate(['asiakkaat', nykyinenAsiakas.avain, 'laskut', 'selaa', rivi.juuriAvain, rivi.avain])
  }

  // lisaa() {

  //   this.laskuService.asetuksetObservable.pipe(
  //     take(1)
  //   ).subscribe(asetukset => {
  //     if (asetukset) {
  //       if (this.asetustenOletuksetService.tarkistaPuuttuukoLaskuasetuksia(asetukset)) {
  //         this.naytaTaydennaAsetuksetDialogi()
  //       } else {
  //         // Sulje dialogi, jos sellainen oli
  //         if (this._taydennaAsetuksetDialogi) {
  //           this._taydennaAsetuksetDialogi.close()
  //           this._taydennaAsetuksetDialogi = null
  //         }
  //         this.router.navigate(['/laskutus/laskut/', 'uusi', 'uusi'])
  //       }
  //     }
  //   })

  // }

  // private naytaTaydennaAsetuksetDialogi() {
  //   if (!this._taydennaAsetuksetDialogi) {
  //     const kaytettavaLeveys = window.innerWidth
  //     const kaytettavaKorkeus = window.innerHeight
  //     const leveys = kaytettavaLeveys > 900 ? '90vw' : '98vw'
  //     const korkeus = kaytettavaKorkeus > 1100 ? '80vh' : kaytettavaKorkeus > 750 ? '85vh' : '92vh'

  //     const settings: MatDialogConfig = {
  //       maxWidth: '900px',
  //       maxHeight: korkeus,
  //       width: leveys,
  //       data: {
  //         mobiili: kaytettavaLeveys < 670
  //       }
  //     }

  //     this._taydennaAsetuksetDialogi = this.dialog.open(LaskuTaydennaAsetuksetDialog, settings)
  //     this._taydennaAsetuksetDialogi.afterClosed().subscribe(result => {
  //       this._taydennaAsetuksetDialogi = null
  //       if (result === 'onnistui') {
  //         this.router.navigate(['/laskutus/laskut/', 'uusi', 'uusi'])
  //       }
  //     })
  //   }
  // }

  async merkitseMaksetuksi(rivi: LaskunListaustietorivi) {
    const kirjanpitaja = await firstValueFrom(this._kirjanpitajaService.kirjautuneenKayttajanKirjanpitajaObservable)
    if (this.vertaaEkaPienempiKuinToka(0, rivi.avoinna)) {
      await this.laskuService.merkitseLaskuTaysinMaksetuksi(rivi, kirjanpitaja)
    } else {
      const data: LaskuHaluatkoVarmastiPeruaMaksetuksiMerkitsemisenLemonatorDialogData = {
        rivi: rivi,
      }
      const settings: MatDialogConfig = {
        data: data,
        panelClass: 'ilman-paddingia',
        maxWidth: '1600px',
        // ,
        // maxHeight: korkeus,
        // width: leveys
      }
      this._pauser.next(false)
      this.dialog
        .open(LaskuHaluatkoVarmastiPeruaMaksetuksiMerkitsemisenLemonatorDialog, settings)
        .afterClosed()
        .subscribe(() => {
          this._pauser.next(true)
        })
    }
  }

  vertaaEkaPienempiKuinToka(pienempi: number, suurempi: number): boolean {
    return this._currencyService.roundHalfUp(pienempi, 2) < this._currencyService.roundHalfUp(suurempi, 2)
  }

  async modifyComment(lasku: Lasku) {
    const kaytettavaLeveys = window.innerWidth
    const kaytettavaKorkeus = window.innerHeight
    const leveys = kaytettavaLeveys > 900 ? '90vw' : '98vw'
    const korkeus = kaytettavaKorkeus > 1100 ? '80vh' : kaytettavaKorkeus > 750 ? '85vh' : '92vh'
    const kirjanpitaja = await firstValueFrom(this._kirjanpitajaService.kirjautuneenKayttajanKirjanpitajaObservable)

    const settings: MatDialogConfig<LaskuKommenttiDialogData> = {
      maxWidth: '900px',
      maxHeight: korkeus,
      width: leveys,
      data: { lasku: lasku, kirjanpitaja: kirjanpitaja },
    }
    if (this._kommenttiDialogi) {
      return
    }
    this._kommenttiDialogi = this.dialog.open(LaskuKommenttiDialog, settings)
    this._kommenttiDialogi.afterClosed().subscribe(result => {
      this._kommenttiDialogi = null
      if (result === 'onnistui') {
        /* NOOP */
      }
    })
  }
}

export interface AsikkaanLaskusummatValuutalleAnimointejaVarten {
  avoinna: number
  eraantynyt: number
  avoinnaAlkuperainen: number
  eraantynytAlkuperainen: number
  valuutta: string
  valuuttaAlkuperainen: string
}

@Injectable()
export class LaskuSummatService {
  private summat: AsikkaanLaskusummatValuutalleAnimointejaVarten = {
    avoinna: 0,
    eraantynyt: 0,
    avoinnaAlkuperainen: 0,
    eraantynytAlkuperainen: 0,
    valuutta: 'EUR',
    valuuttaAlkuperainen: 'EUR',
  }

  private summatSubject = new BehaviorSubject<AsikkaanLaskusummatValuutalleAnimointejaVarten>(this.summat)
  summatObservable = this.summatSubject.asObservable()

  constructor(
    private errorHandler: ErrorHandler,
    private lemonaidFirebase: FirebaseLemonaid,
    private asiakasService: AsiakasService,
  ) {
    this.asiakasService.nykyinenAsiakasAvainObservable
      .pipe(
        switchMap(asiakas => {
          if (asiakas) {
            const uri = 'laskut/summat-new/' + asiakas.avain
            return this.lemonaidFirebase.databaseListenValue<LaskuTotalsPerCurrency>(uri).pipe(
              map(totals => {
                const suurin: { avoinna: number; eraantynyt: number; valuutta: string } = {
                  avoinna: 0,
                  eraantynyt: 0,
                  valuutta: 'EUR',
                }
                if (totals && Object.keys(totals).length > 0) {
                  for (const key of Object.keys(totals)) {
                    const value = totals[key]
                    if (value && suurin.avoinna + suurin.eraantynyt < value.avoinna + value.eraantynyt) {
                      suurin.avoinna = value.avoinna
                      suurin.eraantynyt = value.eraantynyt
                      suurin.valuutta = key
                    }
                  }
                }
                if (suurin) {
                  this.summat.avoinna = suurin.avoinna
                  this.summat.eraantynyt = suurin.eraantynyt
                  this.summat.valuutta = suurin.valuutta
                }
                return this.summat
              }),
            )
          }

          // Ei käyttäjää, nollaa kaikki
          this.summat.avoinna = 0
          this.summat.eraantynyt = 0
          this.summat.avoinnaAlkuperainen = 0
          this.summat.eraantynytAlkuperainen = 0
          this.summat.valuutta = 'EUR'
          this.summat.valuuttaAlkuperainen = 'EUR'

          return of(this.summat)
        }),
      )
      .subscribe(data => {
        this.summatSubject.next(data)
      })
  }

  public numerotRullattiin() {
    if (this.summat.avoinnaAlkuperainen !== this.summat.avoinna || this.summat.eraantynytAlkuperainen !== this.summat.eraantynyt || this.summat.valuuttaAlkuperainen !== this.summat.valuutta) {
      this.summat.avoinnaAlkuperainen = this.summat.avoinna
      this.summat.eraantynytAlkuperainen = this.summat.eraantynyt
      this.summat.valuuttaAlkuperainen = this.summat.valuutta
      this.summatSubject.next(this.summat)
    }
  }
}
